import { useNavigate } from 'react-router-dom'
import { Form, Input, Button, Typography, Alert, Space, Card, message } from 'antd'
import { LockOutlined, LogoutOutlined } from '@ant-design/icons'
import { useMutation } from '@tanstack/react-query'
import { useAuthStore } from '@/features/auth/store'
import { authApi } from '@/features/auth/api'

const { Title, Text } = Typography

interface ChangePasswordForm {
  current_password: string
  new_password: string
  confirm_password: string
}

/**
 * Mandatory change-password gate. Rendered in place of the whole app shell
 * (no sidebar, no other routes) whenever the logged-in user's
 * `must_change_password` flag is true — e.g. auto-provisioned new hires who
 * were created with a temporary password. The user is stuck here until they
 * set a new password; the only escape is Log out.
 *
 * On success we refetch /auth/me and push the fresh user into the store, so
 * the flag flips false and the normal app renders on the next tick.
 */
export default function ForceChangePasswordPage() {
  const navigate = useNavigate()
  const [form] = Form.useForm<ChangePasswordForm>()

  const setUser = useAuthStore((s) => s.setUser)
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const refreshToken = useAuthStore((s) => s.refreshToken)

  const changeMutation = useMutation({
    mutationFn: async (values: ChangePasswordForm) =>
      authApi.changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      }),
    onSuccess: async () => {
      message.success('Password updated')
      // Refetch the current user so must_change_password flips false and the
      // gate lifts. If /me fails for any reason, drop the session and send the
      // user back to login rather than leaving them stuck.
      try {
        const me = await authApi.me()
        setUser(me)
      } catch {
        clearAuth()
        navigate('/login', { replace: true })
      }
    },
  })

  const errorMessage = changeMutation.error
    ? (() => {
        const err = changeMutation.error as { response?: { status?: number; data?: { detail?: unknown } } }
        const detail = err.response?.data?.detail
        if (typeof detail === 'string') return detail
        if (err.response?.status === 400) return 'Current password is incorrect.'
        return 'Could not update your password. Please try again.'
      })()
    : null

  const handleLogout = async () => {
    try {
      await authApi.logout(refreshToken ?? undefined)
    } catch {
      /* ignore */
    }
    clearAuth()
    navigate('/login', { replace: true })
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #001529 0%, #003a6e 60%, #005fa3 100%)',
        padding: '24px 16px',
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 420,
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
        styles={{ body: { padding: '32px 32px' } }}
      >
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                background: 'linear-gradient(135deg, #4096ff, #0958d9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
                boxShadow: '0 4px 16px rgba(9,88,217,0.35)',
              }}
            >
              <LockOutlined style={{ color: '#fff', fontSize: 24 }} />
            </div>
            <Title level={3} style={{ margin: 0, color: '#1a1a2e' }}>
              Set your password
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Your account was created with a temporary password. Please set a new password to continue.
            </Text>
          </div>

          {errorMessage && <Alert type="error" message={errorMessage} showIcon />}

          <Form
            form={form}
            layout="vertical"
            onFinish={(v) => changeMutation.mutate(v)}
            autoComplete="off"
            size="large"
            style={{ marginBottom: 0 }}
          >
            <Form.Item
              name="current_password"
              label="Current (temporary) password"
              style={{ marginBottom: 14 }}
              rules={[{ required: true, message: 'Enter your current password' }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#0958d9' }} />}
                placeholder="Current password"
                style={{ borderRadius: 10 }}
              />
            </Form.Item>

            <Form.Item
              name="new_password"
              label="New password"
              style={{ marginBottom: 14 }}
              rules={[
                { required: true, message: 'Enter a new password' },
                { min: 8, message: 'New password must be at least 8 characters' },
                {
                  validator: (_, value) =>
                    value && value === form.getFieldValue('current_password')
                      ? Promise.reject(new Error('New password must differ from the current password'))
                      : Promise.resolve(),
                },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#0958d9' }} />}
                placeholder="New password (min 8 characters)"
                style={{ borderRadius: 10 }}
              />
            </Form.Item>

            <Form.Item
              name="confirm_password"
              label="Confirm new password"
              dependencies={['new_password']}
              style={{ marginBottom: 20 }}
              rules={[
                { required: true, message: 'Re-enter your new password' },
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    !value || getFieldValue('new_password') === value
                      ? Promise.resolve()
                      : Promise.reject(new Error('The two passwords do not match')),
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#0958d9' }} />}
                placeholder="Confirm new password"
                style={{ borderRadius: 10 }}
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              block
              loading={changeMutation.isPending}
              style={{
                height: 48,
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #4096ff, #0958d9)',
                border: 'none',
                boxShadow: '0 4px 14px rgba(9,88,217,0.4)',
              }}
            >
              Update password
            </Button>
          </Form>

          <Button
            type="text"
            block
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{ color: '#8c8c8c' }}
          >
            Log out
          </Button>
        </Space>
      </Card>
    </div>
  )
}
